import { useMemo } from 'react'

import { LegacyFeatures, Screenshot, SimpleFeature, useGetScreenshotsByGameIdsQuery } from '../../../api/core'
import languageService from '../../../services/LanguageService'
import { Game } from '../../game'
import { useGetGameList } from '../../game/hooks/gameHooks'
import { FeatureGame } from '../../game/types/Game'
import { convertLegacyScreenshots, getCombinedScreenshots } from '../../implementation-examples/util/screenshotHelpers'
import { NewsEntryScreenshotFeature } from '../../news/types/UnifiedNewsEntry'

export enum ScreenshotDataSortedOpts {
  ASC,
  DESC,
}

interface UseScreenshotDataProps {
  gameIds: string[]
  sorted?: ScreenshotDataSortedOpts
}
export const useScreenshotData = ({ gameIds, sorted }: UseScreenshotDataProps) => {
  const { data: screenshotsData = [], isFetching: isFetchingScreenshots } = useGetScreenshotsByGameIdsQuery(gameIds)
  const sortedScreenshotsData = useMemo(() => {
    return [...screenshotsData]
      .filter((screenshot) => !!screenshot.versionReleaseDate)
      .sort((a, b) => {
        if (a.versionReleaseDate && b.versionReleaseDate) {
          switch (sorted) {
            case ScreenshotDataSortedOpts.DESC:
              return b.versionReleaseDate - a.versionReleaseDate
            default:
              return a.versionReleaseDate - b.versionReleaseDate
          }
        }
        return 0
      })
  }, [screenshotsData, sorted])

  if (!sorted) {
    return { screenshotsData, isFetching: isFetchingScreenshots }
  }

  return { screenshotsData: sortedScreenshotsData, isFetching: isFetchingScreenshots }
}

const useScreenshotsUniqueByGameId = (sortedScreenshotsData: Screenshot[], maxScreenshotCount: number) => {
  const screenshotsUniqueByGameId = useMemo(() => {
    if (!sortedScreenshotsData.length) {
      return []
    }

    const tempScreenshotsData: { screenshots: Screenshot[]; uniqueGamesByScreenshots: string[] } = {
      screenshots: [],
      uniqueGamesByScreenshots: [],
    }

    for (let i = 0; i <= sortedScreenshotsData.length; i++) {
      const screenshot = sortedScreenshotsData[i]
      if (!screenshot) {
        continue
      }

      if (screenshot.gameId && screenshot.gameVersion && !tempScreenshotsData.uniqueGamesByScreenshots.includes(screenshot.gameId)) {
        tempScreenshotsData.uniqueGamesByScreenshots.push(screenshot.gameId)
        tempScreenshotsData.screenshots.push(screenshot)
      }

      if (tempScreenshotsData.screenshots.length === maxScreenshotCount) {
        break
      }
    }

    return tempScreenshotsData.screenshots
  }, [maxScreenshotCount, sortedScreenshotsData])
  return screenshotsUniqueByGameId
}

export type NewsEntriesFeatureByGameIds = {
  [gameId: string]: {
    features: NewsEntryScreenshotFeature[]
    version?: string
  }
}

/**
 * Translate the LegacyFeatures map attached to a single Screenshot
 * into the localized NewsEntryScreenshotFeature[] shape consumed by
 * `ScreenshotPreview`'s "Related features" block.
 *
 * The Screenshot modal (`ScreenshotsModalContainer` -> `RelatedFeatureList`)
 * derives its features from this same `screenshot.features` map (see
 * `getScreenshotList` in `src/api/core.ts`). Sourcing the card's related
 * features from the same place keeps card and modal in sync (GR-2100).
 */
const legacyFeatureToNewsEntryFeature = (featureId: string, choiceId: number): NewsEntryScreenshotFeature => {
  const featureName = languageService.getTranslation('features', featureId)
  const choiceName = languageService.getTranslation('choices', `${choiceId}`)
  return {
    featureLegacyId: parseInt(featureId, 10),
    featureName,
    featureName_translation: featureName,
    choiceLegacyId: choiceId,
    choiceName,
    choiceName_translation: choiceName,
  }
}

const legacyFeaturesToNewsEntryFeatures = (features: LegacyFeatures): NewsEntryScreenshotFeature[] =>
  Object.entries(features).map(([featureId, choiceId]) => legacyFeatureToNewsEntryFeature(featureId, choiceId))

const simpleFeatureToNewsEntryFeature = (feature: SimpleFeature): NewsEntryScreenshotFeature => {
  const featureName = feature.featureName ?? languageService.getTranslation('features', `${feature.featureLegacyId}`)
  const choiceName = feature.choiceName ?? languageService.getTranslation('choices', `${feature.choiceLegacyId}`)
  return {
    ...feature,
    featureName,
    featureName_translation: featureName,
    choiceName,
    choiceName_translation: choiceName,
  }
}

/**
 * `Screenshot.features` (see `src/api/core.ts`) can be one of three shapes:
 *   - `LegacyFeatures` map (`{ [featureLegacyId: string]: choiceLegacyId }`)
 *   - `LegacyFeatures[]` (array of those maps)
 *   - `SimpleFeature[]` (already-localized feature objects)
 * Handle all three so related features are never silently dropped (GR-2100).
 */
const screenshotFeaturesToNewsEntryFeatures = (features: Screenshot['features']): NewsEntryScreenshotFeature[] => {
  if (!features) {
    return []
  }

  if (!Array.isArray(features)) {
    return legacyFeaturesToNewsEntryFeatures(features)
  }

  return features.flatMap((entry) => {
    // `LegacyFeatures[]` entries are plain `{ [legacyId]: choiceId }` maps,
    // whereas `SimpleFeature[]` entries carry a `featureLegacyId` field.
    if (typeof entry === 'object' && entry !== null && 'featureLegacyId' in entry) {
      return [simpleFeatureToNewsEntryFeature(entry as SimpleFeature)]
    }
    return legacyFeaturesToNewsEntryFeatures(entry as LegacyFeatures)
  })
}

const useNewsEntriesFeatureByGameIds = (screenshots: Screenshot[]) => {
  const newsEntriesFeatureByGameIds = useMemo(() => {
    return screenshots.reduce((result, screenshot) => {
      if (!screenshot.gameId || !screenshot.gameVersion) {
        return result
      }
      const features = screenshotFeaturesToNewsEntryFeatures(screenshot.features)
      if (!features.length) {
        return result
      }
      result[screenshot.gameId] = { features, version: screenshot.gameVersion }
      return result
    }, {} as NewsEntriesFeatureByGameIds)
  }, [screenshots])

  return { newsEntriesFeatureByGameIds, isLoading: false }
}

interface UseGetFeatureWithScreenshotGames {
  gameIds: string[]
  maxScreenshotCount: number
}
export const useGetFeatureWithScreenshotGames = ({ gameIds, maxScreenshotCount }: UseGetFeatureWithScreenshotGames) => {
  const { data: gamesData = [], isFetching: isFetchingGames } = useGetGameList({
    gameIds,
    include: 'name,artist,icons,conventionalSubgenre,conventionalSubgenreId,sdranks,sranks,srank,appId',
  })

  const { screenshotsData: sortedScreenshotsData, isFetching: isFetchingScreenshots } = useScreenshotData({
    gameIds,
    sorted: ScreenshotDataSortedOpts.DESC,
  })

  const screenshotsUniqueByGameId = useScreenshotsUniqueByGameId(sortedScreenshotsData, maxScreenshotCount)
  const { newsEntriesFeatureByGameIds, isLoading: isNewsEntriesLoading } = useNewsEntriesFeatureByGameIds(screenshotsUniqueByGameId)

  const featureGames: FeatureGame[] = useMemo(() => {
    return (
      [...gamesData]
        .map((game: Game) => {
          const screenshotsForGame = convertLegacyScreenshots((screenshotsUniqueByGameId || []).filter(({ gameId }) => gameId === game.id))

          return {
            ...game,
            featureScreenshots: screenshotsForGame.filter((screenshot) => screenshot.features?.length),
            conceptScreenshots: screenshotsForGame.filter((screenshot) => screenshot.conceptIds?.length),
          } as FeatureGame
        })
        // filter games that do not have any feature screenshots or concept screenshots
        .filter((game) => game.featureScreenshots.length || game.conceptScreenshots.length)
        .sort((a, b) => {
          const aCombinedShots = getCombinedScreenshots(a)
          const bCombinedShots = getCombinedScreenshots(b)

          if (aCombinedShots[0].versionReleaseDate && bCombinedShots[0].versionReleaseDate) {
            return bCombinedShots[0].versionReleaseDate - aCombinedShots[0].versionReleaseDate
          }
          return 0
        })
    )
  }, [gamesData, screenshotsUniqueByGameId])

  const featureGameIds = useMemo(() => featureGames.map((featureGame) => featureGame.id), [featureGames])
  const sortedScreenshotUniqueByGameId = useMemo(() => {
    return screenshotsUniqueByGameId
      .filter((screenshot) => featureGameIds.includes(screenshot.gameId as string))
      .sort((a, b) => featureGameIds.indexOf(a.gameId as string) - featureGameIds.indexOf(b.gameId as string))
  }, [featureGameIds, screenshotsUniqueByGameId])

  return {
    newsEntriesFeatureByGameIds,
    screenshotsUniqueByGameId: sortedScreenshotUniqueByGameId,
    featureGames,
    isLoading: isFetchingScreenshots || isFetchingGames || isNewsEntriesLoading,
  }
}
